import React, { CSSProperties, forwardRef, useMemo, useRef, useImperativeHandle } from "react";
import { Button as PrimeButton, ButtonProps as PrimeButtonProps } from "primereact/button";
import {
  getColorBaseTokenValue,
  type ColorBaseToken,
  type Severity,
  SEVERITY_TO_PRIME_MAP,
  mergeClassNames,
  hexToRgba
} from "../../design-tokens";
import styles from "./Button.module.css";

/**
 * Variantes visuais do botão
 * - filled: Preenchido com cor sólida (padrão)
 * - outlined: Apenas borda, fundo transparente
 * - text: Sem borda e sem fundo (link-style)
 */
export type ButtonVariant = "filled" | "outlined" | "text";

/**
 * Paleta de cores para cada severity
 */
type SeverityPalette = {
  base100: ColorBaseToken;
  base300: ColorBaseToken;
  base500: ColorBaseToken;
  base700: ColorBaseToken;
  base900: ColorBaseToken;
};

/**
 * Mapeamento de severities para tokens de cor
 */
const SEVERITY_TOKENS: Record<Severity, SeverityPalette> = {
  primary: {
    base100: "color.primary.100",
    base300: "color.primary.300",
    base500: "color.primary.500",
    base700: "color.primary.700",
    base900: "color.primary.900"
  },
  secondary: {
    base100: "color.secondary.100",
    base300: "color.secondary.300",
    base500: "color.secondary.500",
    base700: "color.secondary.700",
    base900: "color.secondary.900"
  },
  neutral: {
    base100: "color.neutral.100",
    base300: "color.neutral.300",
    base500: "color.neutral.500",
    base700: "color.neutral.700",
    base900: "color.neutral.900"
  },
  success: {
    base100: "color.feedback.success.100",
    base300: "color.feedback.success.300",
    base500: "color.feedback.success.500",
    base700: "color.feedback.success.700",
    base900: "color.feedback.success.900"
  },
  warning: {
    base100: "color.feedback.warning.100",
    base300: "color.feedback.warning.300",
    base500: "color.feedback.warning.500",
    base700: "color.feedback.warning.700",
    base900: "color.feedback.warning.900"
  },
  danger: {
    base100: "color.feedback.danger.100",
    base300: "color.feedback.danger.300",
    base500: "color.feedback.danger.500",
    base700: "color.feedback.danger.700",
    base900: "color.feedback.danger.900"
  }
};

/**
 * Props do componente Button
 * Omite props do PrimeReact que não queremos expor
 */
export interface ButtonProps
  extends Omit<
    PrimeButtonProps,
    "severity" | "text" | "outlined" | "plain" | "link" | "raised" | "rounded" | "iconPos" | "size"
  > {
  /**
   * Texto exibido no botão (opcional para botões de apenas ícone)
   */
  label?: string;

  /**
   * Define a intenção visual do botão
   * @default "primary"
   */
  severity?: Severity;

  /**
   * Variante visual do botão
   * @default "filled"
   */
  variant?: ButtonVariant;

  /**
   * Nome do ícone Material Symbols (versão outlined)
   * Para variante "icon", é obrigatório ter ícone
   */
  icon?: string;

  /**
   * Posição do ícone em relação ao label
   * @default "left"
   */
  iconPos?: "left" | "right";

  /**
   * Label acessível (aria-label)
   * Opcional (usa label como fallback)
   */
  "aria-label"?: string;
}

/**
 * Cria variáveis CSS para estilização dinâmica baseada em severity e variant
 */
const createStyleVariables = (
  severity: Severity,
  variant: ButtonVariant
): CSSProperties => {
  const palette = SEVERITY_TOKENS[severity];
  const color100 = getColorBaseTokenValue(palette.base100);
  const color300 = getColorBaseTokenValue(palette.base300);
  const color500 = getColorBaseTokenValue(palette.base500);
  const color700 = getColorBaseTokenValue(palette.base700);
  const color900 = getColorBaseTokenValue(palette.base900);

  const isFilled = variant === "filled";
  const isText = variant === "text";

  // Background
  const bg = isFilled ? color500 : "transparent";
  const hoverBg = isFilled ? color300 : hexToRgba(color300, 0.16);
  const activeBg = isFilled ? color700 : hexToRgba(color500, 0.24);

  // Border
  const border = isText ? "transparent" : color500;
  const hoverBorder = isText ? "transparent" : color300;
  const activeBorder = isText ? "transparent" : isFilled ? color700 : color500;

  // Text color
  const textColor = isFilled
    ? "var(--color-neutral-000, #ffffff)"
    : color500;

  return {
    "--ds-button-bg": bg,
    "--ds-button-border": border,
    "--ds-button-color": textColor,
    "--ds-button-hover-bg": hoverBg,
    "--ds-button-hover-border": hoverBorder,
    "--ds-button-hover-color": isFilled
      ? "var(--color-neutral-000, #ffffff)"
      : color500,
    "--ds-button-active-bg": activeBg,
    "--ds-button-active-border": activeBorder,
    "--ds-button-focus-border": color900,
    "--ds-button-focus-ring": hexToRgba(color900, 0.35),
    "--ds-button-disabled-bg": color100,
    "--ds-button-disabled-border": color100,
    "--ds-button-disabled-color": "var(--color-neutral-500)",
    "--ds-button-loading-bg": color100,
    "--ds-button-loading-border": color100,
    "--ds-button-loading-color": color500
  } as CSSProperties;
};

/**
 * Renderiza ícone Material Symbols
 */
const renderIcon = (icon?: string): React.ReactNode => {
  if (!icon) return undefined;

  return (
    <span
      className="material-symbols-outlined"
      aria-hidden="true"
      style={{ fontSize: "24px" }}
    >
      {icon}
    </span>
  );
};

/**
 * Button Component
 * 
 * Componente de botão unificado do Design System.
 * Suporta 3 variantes: filled, outlined e text.
 * 
 * @example
 * // Botão primário preenchido
 * <Button label="Confirmar" severity="primary" />
 * 
 * @example
 * // Botão outlined com ícone
 * <Button label="Editar" severity="secondary" variant="outlined" icon="edit" />
 * 
 * @example
 * // Botão text (link-style)
 * <Button label="Cancelar" variant="text" />
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      label,
      severity = "primary",
      variant = "filled",
      icon,
      iconPos = "left",
      className,
      style,
      loading,
      disabled,
      "aria-label": ariaLabel,
      ...rest
    },
    ref
  ) => {
    // Ref interno para capturar o elemento DOM do PrimeButton
    const buttonRef = useRef<any>(null);

    // Expõe o elemento DOM através do ref externo
    useImperativeHandle(ref, () => {
      // PrimeButton expõe o elemento DOM através de getElement()
      return buttonRef.current?.getElement?.() || buttonRef.current;
    }, []);

    // Monta classes CSS
    const classNames = mergeClassNames(
      styles.root,
      styles[variant],
      loading && styles.loading,
      className
    );

    // Cria variáveis CSS dinâmicas
    const styleVariables = useMemo<CSSProperties>(
      () => ({
        ...createStyleVariables(severity, variant),
        ...style
      }),
      [severity, variant, style]
    );

    return (
      <PrimeButton
        {...rest}
        ref={buttonRef}
        className={classNames}
        style={styleVariables}
        label={label}
        icon={renderIcon(icon)}
        iconPos={iconPos}
        loading={loading}
        disabled={disabled}
        text={variant === "text"}
        outlined={variant === "outlined"}
        aria-label={ariaLabel || label}
        severity={SEVERITY_TO_PRIME_MAP[severity] as any}
        data-ds-variant={variant}
        data-ds-severity={severity}
      />
    );
  }
);

Button.displayName = "Button";

export default Button;
